import invariant from 'invariant';

import { decodeChannelMessage } from '@mythos/api/WireDecoder';
import { ClientMessage } from '@mythos/common/generated/bebop';
import Session from '@mythos/utils/Session';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import nullthrows from 'nullthrows';
import { useChannelStore } from './stores/channelStore';
import { useConnectionStore } from './stores/connectionStore';

class ChannelListener {
  private started_: boolean = false;
  private session_: Session | null = null;

  private ws_: WebSocket | null = null;
  private subscriptions_: Set<string> = new Set();
  private retryCount_: number = 0;
  private maxRetryDelayMS_: number = 30000; // 30 seconds
  private retryTimeoutID_: ReturnType<typeof setTimeout> | null = null;
  private isConnecting_: boolean = false;

  constructor() {}

  async sendChatMessage(room: string, text: string): Promise<void> {
    if (!this.ws_) {
      return;
    }
    this.ws_.send(ClientMessage.fromChat({ room, text }).encode());
  }

  async subscribeToTopic(topic: string): Promise<void> {
    if (this.subscriptions_.has(topic)) {
      return;
    }

    this.subscriptions_.add(topic);

    if (this.ws_) {
      this._sendSubscribe(this.ws_, topic);
    }
  }
  async unsubscribeToTopic(topic: string): Promise<void> {
    if (!this.subscriptions_.has(topic)) {
      return;
    }
    this.subscriptions_.delete(topic);

    if (this.ws_) {
      this._sendUnsubscribe(this.ws_, topic);
    }
  }

  private _sendSubscribe(ws: WebSocket, topic: string): void {
    ws.send(ClientMessage.fromSubscribe({ topic }).encode());
  }

  private _sendUnsubscribe(ws: WebSocket, topic: string): void {
    ws.send(ClientMessage.fromUnsubscribe({ topic }).encode());
  }

  private _onWSError = (e: Event) => {
    console.error('websocket error', e);
  };

  private _onWSMessage = (e: MessageEvent) => {
    const message = decodeChannelMessage(e.data as ArrayBuffer);
    if (message) {
      useChannelStore.getState().handleMessage(message);
    }
  };

  private _onWSClose = async (e: Event) => {
    console.error('websocket close', e);
    this.ws_ = null;
    this.isConnecting_ = false;
    useConnectionStore.getState().setStatus('disconnected');

    // Exponential backoff: delay = min(2^retryCount * 500ms, maxRetryDelayMS)
    const baseDelayMS = 500; // 0.5 second base
    const delayMS = Math.min(
      Math.pow(2, this.retryCount_) * baseDelayMS,
      this.maxRetryDelayMS_,
    );

    console.log(
      `Retrying websocket connection in ${delayMS}ms (attempt ${this.retryCount_ + 1})`,
    );

    this.retryTimeoutID_ = setTimeout(async () => {
      this.retryTimeoutID_ = null;
      this.retryCount_++;
      try {
        await this._connect();
      } catch (error) {
        console.error('Failed to reconnect websocket:', error);
      }
    }, delayMS);
  };

  private async _connect(): Promise<void> {
    this.isConnecting_ = true;
    const currentStatus = useConnectionStore.getState().status;
    // Don't show banner immediately on initial connection - wait a bit
    if (currentStatus === 'initial') {
      setTimeout(() => {
        if (this.isConnecting_) {
          useConnectionStore.getState().setStatus('connecting');
        }
      }, 2000);
    } else {
      useConnectionStore.getState().setStatus('connecting');
    }

    const url = this._getURL();
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';

    // Wait for WebSocket to open
    await new Promise<void>((resolve, reject) => {
      ws.onopen = () => {
        console.log('websocket connected, sending auth');
        resolve();
      };
      ws.onerror = (e) => {
        reject(e);
      };
    });

    // Send auth message
    const token = await this._genJWT();
    if (token) {
      ws.send(ClientMessage.fromAuthUser({ jwt: token }).encode());
    } else {
      ws.send(ClientMessage.fromAuthAnonymous({}).encode());
    }

    // Wait for auth response
    await new Promise<void>((resolve, reject) => {
      const authHandler = (e: MessageEvent) => {
        const message = decodeChannelMessage(e.data as ArrayBuffer);
        if (message?.type === 'authSuccess') {
          console.log(
            'channel auth success, userID:',
            message.userID || '(anonymous)',
          );
          ws.removeEventListener('message', authHandler);
          resolve();
        } else if (message?.type === 'authError') {
          console.error('channel auth error:', message.message);
          ws.removeEventListener('message', authHandler);
          reject(new Error(message.message));
        }
        // Ignore other messages during auth phase
      };
      ws.addEventListener('message', authHandler);

      ws.onerror = (e) => {
        ws.removeEventListener('message', authHandler);
        reject(e);
      };
    });

    // Auth complete, set up normal handlers
    ws.onmessage = this._onWSMessage;
    ws.onerror = this._onWSError;
    ws.onclose = this._onWSClose;

    this.ws_ = ws;
    this.isConnecting_ = false;
    this.retryCount_ = 0;
    useConnectionStore.getState().setStatus('connected');

    // Now subscribe to topics
    for (let topic of this.subscriptions_) {
      this._sendSubscribe(ws, topic);
    }
  }

  private _getURL(): string {
    let protocol = 'ws:';
    let port = ':3002';
    if (window.location.protocol === 'https:') {
      protocol = 'wss:';
      port = '';
    }
    return protocol + '//' + window.location.hostname + port + '/channel';
  }

  private async _genJWT(): Promise<string | null> {
    if (!this.session_) {
      return null;
    }
    return this._genFetchJWT();
  }

  private async _genFetchJWT(): Promise<string> {
    if (window && window.localStorage) {
      const cachedJWT = window.localStorage.getItem('channelJwt');
      if (this._isValidJWT(cachedJWT)) {
        return cachedJWT!;
      }
    }

    const response = await fetch('/auth/jwt', { credentials: 'same-origin' });
    const jwt = await response.text();
    if (!this._isValidJWT(jwt)) {
      throw new Error('Invalid JWT');
    }
    if (window && window.localStorage) {
      window.localStorage.setItem('channelJwt', jwt);
    }
    return jwt;
  }
  private _isValidJWT(token: string | null): boolean {
    if (!token) {
      return false;
    }
    let decoded: JwtPayload | null = null;
    try {
      decoded = jwtDecode(token);
    } catch (e) {
      return false;
    }
    if (!decoded) {
      return false;
    }

    return decoded.sub === this.session_?.getUserID();
  }

  retry(): void {
    if (this.isConnecting_ || this.ws_) {
      return;
    }

    // Cancel any pending retry timeout
    if (this.retryTimeoutID_) {
      clearTimeout(this.retryTimeoutID_);
      this.retryTimeoutID_ = null;
    }

    this._connect().catch((error) => {
      console.error('Failed to connect websocket:', error);
    });
  }

  start(session: Session | null): this {
    invariant(!this.started_, 'already started');

    this.started_ = true;
    this.session_ = session || null;
    this._connect();

    return this;
  }
}

let channelListener: ChannelListener | null = null;
export function startChannelListener(session: Session | null): ChannelListener {
  if (!channelListener) {
    channelListener = new ChannelListener().start(session);
  }
  return channelListener;
}

export function getChannelListener(): ChannelListener {
  return nullthrows(channelListener);
}
